import React, { createContext, useEffect, useReducer } from 'react';
import SplashScreen from 'src/devias/components/SplashScreen';
import { client } from '../services/apiClient';
import AuthService from 'src/services/AuthService';
import ProfileService from 'src/services/ProfileService';
import { deleteCookie, setCookie, getCookie } from 'src/devias/utils/cookies';

const initialAuthState = {
  isAuthenticated: false,
  isNewUser: false,
  isInitialized: false,
  user: null,
  isSigninPopupOpen: false
};

const setSession = accessToken => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    client.defaults.headers.common.Authorization = `${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    delete client.defaults.headers.common.Authorization;
  }
};

const setId = id => {
  if (id) {
    localStorage.setItem('userId', id);
  } else {
    localStorage.removeItem('userId');
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'INITIALIZE': {
      const { isAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        user,
        isNewUser: false
      };
    }
    case 'SEND_OTP': {
      const { user } = action.payload;
      return {
        ...state,
        user
      };
    }
    case 'VERIFY_OTP': {
      const { user, isAuthenticated } = action.payload;
      return {
        ...state,
        user: { ...state.user, ...user },
        isAuthenticated,
        isNewUser: user.new_record
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        isAuthenticated: false,
        user: null
      };
    }
    case 'SET_NEW_USER': {
      const { isNewUser } = action.payload;
      return {
        ...state,
        isNewUser
      };
    }
    case 'TOGGLE_SIGNIN_POPUP': {
      const { isSigninPopupOpen } = action.payload;
      return {
        ...state,
        isSigninPopupOpen
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  method: 'JWT',
  sendOtp: phoneNumber => Promise.resolve(),
  verifyOtp: (phoneNumber, otp) => Promise.resolve(),
  resendOtp: (phoneNumber, type) => Promise.resolve(),
  setNewUser: isNewUser => {},
  logout: () => {},
  toggleSignInPopup: isOpen => {}
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const sendOtp = async phoneNumber => {
    setSession(); // removing token
    setId(); // removing id
    const response = await AuthService.sendOtp({
      phone_number: phoneNumber
    });
    const { phone_number } = response;
    dispatch({
      type: 'SEND_OTP',
      payload: {
        user: {
          phone_number
        }
      }
    });
  };

  const verifyOtp = async (phone_number, otp) => {
    // handle error here
    try {
      const response = await AuthService.verifyOtp({
        phone_number,
        otp
      });
      const { confirmed, auth_token, new_record, id } = response;
      dispatch({
        type: 'VERIFY_OTP',
        payload: {
          user: {
            phone_number: phone_number,
            confirmed,
            auth_token,
            new_record,
            id
          },
          isAuthenticated: true
        }
      });
      setSession(auth_token);
      setId(id);
    } catch (error) {
      dispatch({
        type: 'VERIFY_OTP',
        payload: {
          user: {
            confirmed: false
          },
          isAuthenticated: false
        }
      });
    }
  };

  const resendOtp = async (phone_number, type) => {
    AuthService.resendOtp({
      phone_number,
      type
    });
  };

  const logout = () => {
    if (state.user && state.user.id) {
      AuthService.logout(state.user.id);
      dispatch({
        type: 'LOGOUT'
      });
      setSession();
      setId();
    }
  };

  const setNewUser = isNewUser => {
    dispatch({
      type: 'SET_NEW_USER',
      payload: {
        isNewUser
      }
    });
  };

  const toggleSignInPopup = isSigninPopupOpen => {
    dispatch({
      type: 'TOGGLE_SIGNIN_POPUP',
      payload: {
        isSigninPopupOpen
      }
    });
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken');
        const id = window.localStorage.getItem('userId');
        const cookiesUserId = getCookie('userId');
        if ((accessToken && id) || cookiesUserId) {
          setSession(accessToken);
          setId(id || cookiesUserId);
          const response = await ProfileService.getProfile(id);
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: response
            }
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null
            }
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  if (!state.isInitialized) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        sendOtp,
        verifyOtp,
        resendOtp,
        logout,
        setNewUser,
        toggleSignInPopup
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
